import React from "react";
import { Users, Award, Heart, Target } from "lucide-react";

const AboutPage: React.FC = () => {
  return (
    <div className="container mx-auto px-4 py-12">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold mb-8 text-center">About Jog N Gym</h1>
        
        {/* Mission Statement */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-12">
          <h2 className="text-2xl font-bold mb-4 text-emerald-700">Our Mission</h2>
          <p className="text-lg text-slate-700 leading-relaxed">
            At Jog N Gym, our mission is to make quality fitness and health products accessible to everyone. 
            We believe that a healthy lifestyle should be attainable regardless of your fitness level or experience. 
            Our carefully curated selection of products is designed to support your wellness journey, 
            whether you're just starting out or are a seasoned fitness enthusiast.
          </p>
        </div>
        
        {/* Our Story */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6">Our Story</h2>
          <div className="bg-slate-50 rounded-lg p-8">
            <p className="text-slate-700 mb-4 leading-relaxed">
              Jog N Gym was founded in 2018 by a group of fitness enthusiasts who were frustrated with the lack of 
              reliable, high-quality fitness products in the market. What started as a small online store with just 
              a handful of products has now grown into a comprehensive health and fitness marketplace.
            </p>
            <p className="text-slate-700 mb-4 leading-relaxed">
              Our journey began with a simple idea: create a one-stop shop where fitness enthusiasts could find 
              products they could trust. We personally test every product we sell to ensure it meets our strict 
              quality standards.
            </p>
            <p className="text-slate-700 leading-relaxed">
              Today, we're proud to serve thousands of customers worldwide, helping them achieve their fitness goals 
              with products that are effective, durable, and fairly priced.
            </p>
          </div>
        </div>
        
        {/* Our Values */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6">Our Values</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="flex items-center mb-4">
                <div className="bg-emerald-100 p-3 rounded-full mr-4">
                  <Quality className="h-6 w-6 text-emerald-600" />
                </div>
                <h3 className="text-xl font-semibold">Quality</h3>
              </div>
              <p className="text-slate-600">
                We never compromise on quality. Every product in our catalog is thoroughly tested and vetted.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="flex items-center mb-4">
                <div className="bg-emerald-100 p-3 rounded-full mr-4">
                  <Heart className="h-6 w-6 text-emerald-600" />
                </div>
                <h3 className="text-xl font-semibold">Integrity</h3>
              </div>
              <p className="text-slate-600">
                We're honest about what our products can and cannot do. No false promises or exaggerated claims.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="flex items-center mb-4">
                <div className="bg-emerald-100 p-3 rounded-full mr-4">
                  <Users className="h-6 w-6 text-emerald-600" />
                </div>
                <h3 className="text-xl font-semibold">Community</h3>
              </div>
              <p className="text-slate-600">
                We believe in building a community of like-minded individuals who support each other's fitness journeys.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="flex items-center mb-4">
                <div className="bg-emerald-100 p-3 rounded-full mr-4">
                  <Target className="h-6 w-6 text-emerald-600" />
                </div>
                <h3 className="text-xl font-semibold">Innovation</h3>
              </div>
              <p className="text-slate-600">
                We're constantly on the lookout for innovative products that can help our customers achieve their goals more effectively.
              </p>
            </div>
          </div>
        </div>
        
        {/* Team Section */}
        <div>
          <h2 className="text-2xl font-bold mb-6">Our Team</h2>
          <div className="bg-white rounded-lg shadow-md p-8">
            <p className="text-slate-700 mb-6 leading-relaxed">
              Behind Jog N Gym is a team of dedicated professionals who are passionate about health and fitness. 
              Our team includes certified fitness trainers, nutritionists, and product specialists who work together 
              to bring you the best fitness products on the market.
            </p>
            <p className="text-slate-700 leading-relaxed">
              We're not just selling products – we're building a movement. A movement that encourages people to take 
              control of their health and fitness, to push their limits, and to become the best versions of themselves.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Custom icon component
const Quality: React.FC<{ className?: string }> = ({ className }) => (
  <Award className={className} />
);

export default AboutPage;